import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { internalHrefFor, isInternalHref } from "@/lib/legacy-redirects";

/**
 * Link for an address held in a database column (the award "Full story"
 * links). The stored value may be a site path, an old miinews.com address or
 * a real external link; internalHrefFor() works out which.
 *
 *   on this site -> router link, same tab
 *   external     -> plain anchor in a new tab, as before
 *
 * `children` may be a function of `external`, so the caller can show the
 * external-link icon only when the link really leaves the site.
 */
export function StoredLink({
  url,
  className,
  children,
}: {
  url: string;
  className?: string;
  children: ReactNode | ((external: boolean) => ReactNode);
}) {
  const href = internalHrefFor(url);
  const external = !isInternalHref(href);
  const body = typeof children === "function" ? children(external) : children;
  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {body}
      </a>
    );
  }
  // Link's `to` is a pathname. A path carrying a query or a hash goes through
  // a plain same-tab anchor so the router does not read them as part of the slug.
  return /[?#]/.test(href) ? (
    <a href={href} className={className}>
      {body}
    </a>
  ) : (
    <Link to={href} className={className}>
      {body}
    </Link>
  );
}
